import { exportChangeLogColours, setChangeLogComment } from '@components/export/export.config';
import * as GC from '@grapecity/spread-sheets';
import { IBetaCalibration } from '@models/beta/beta-estimate';
import { IMarketRate } from '@models/beta/marketrate';
import { ICalibrationChange } from '@models/calibrations/calibration-changelog';
import { IProduct } from '@models/product';
import { CalibrationStatus } from '@models/record-status';
import { borders, colours, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { changeLogColours, getCalibrationRecordStatusBorderColor, getChangeLogSummary, getMarketRateNameById } from '../helpers';

let _marketRates: IMarketRate[];
const COLSPAN = 3;

export const addBetaChangeLogToSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  product: IProduct,
  calibrations: IBetaCalibration[],
  marketRates: IMarketRate[],
  // tslint:disable-next-line: ban-types
  getBetaChangeLogDiff: Function
): number => {
  try {
    _marketRates = marketRates;

    rowIndex = createHeaders(sheet, rowIndex, startCol);
    [...calibrations].forEach((calibration: IBetaCalibration, levelOneIndex) => {
      if (levelOneIndex === calibrations.length - 1) {
        rowIndex = createLevelOneCalibration(sheet, rowIndex, startCol, calibration, marketRates);
      } else {
        rowIndex = createLevelOneCalibration(
          sheet,
          rowIndex,
          startCol,
          calibration,
          marketRates,
          getBetaChangeLogDiff(calibration, calibrations[levelOneIndex + 1])
        );
      }

      // Fix 7641: The last inactive record should not be expanded in the export
      if (calibration.RecordStatus !== CalibrationStatus.Inactive) {
        calibration.ChangeLogs.forEach((changeLog, ind) => {
          const isLastOfList = ind === calibration.ChangeLogs.length - 1;
          rowIndex = createLevelTwoCalibration(sheet, rowIndex, startCol, changeLog, isLastOfList);
        });
      }
    });
  } catch (err) {
    console.error('Error building beta changelog for export', err);
  }

  return rowIndex;
};

function createHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colStart: number): number {
  try {
    let takenCols = 0;

    // Market Rate
    sheet.getCell(rowIndex, colStart + takenCols).text('Market Rate');
    sheet.addSpan(rowIndex, colStart + takenCols, 1, COLSPAN);
    takenCols += COLSPAN;

    // Lag
    sheet.getCell(rowIndex, colStart + takenCols).text('Lag');
    sheet.addSpan(rowIndex, colStart + takenCols, 1, COLSPAN);
    takenCols += COLSPAN;

    // Beta
    sheet.getCell(rowIndex, colStart + takenCols).text('Beta Coeff.');
    sheet.addSpan(rowIndex, colStart + takenCols, 1, COLSPAN);
    takenCols += COLSPAN;

    // Styling
    const headerArea = sheet.getRange(rowIndex, colStart, 1, takenCols);
    headerArea
      .backColor(changeLogColours.HeaderColour)
      .foreColor('white')
      .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center);
    headerArea.setBorder(borders.BLACK_MEDIUM, { all: true });
  } catch (err) {
    console.error('Error building headers', sheet.name(), err);
  }

  rowIndex++;
  return rowIndex;
}

function createLevelOneCalibration(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  changeLog: any,
  marketRates: IMarketRate[],
  changedProps?: ICalibrationChange[]
): number {
  let takenCols = 0;
  const rowSpan = 2;
  const takenRows = rowSpan;

  try {
    // Market Rate
    const marketRateName = getMarketRateNameById(changeLog.MarketRateId, marketRates);
    sheet.getCell(rowIndex, startCol + takenCols).text(marketRateName);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, COLSPAN);

    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'MarketRateId');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }

    takenCols += COLSPAN;

    // Lag
    sheet.getCell(rowIndex, startCol + takenCols).text(`${changeLog.Lag} - Months`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, COLSPAN);

    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'Lag');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }

    takenCols += COLSPAN;

    // Beta Coefficient
    sheet.getCell(rowIndex, startCol + takenCols).value(`${(Math.round(changeLog.BetaCoefficient * 100) / 100).toFixed(2)}`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, COLSPAN);

    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'BetaCoefficient');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }

    takenCols += COLSPAN;

    // Range Styling
    const rowRange = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
    rowRange.vAlign(GC.Spread.Sheets.VerticalAlign.center).hAlign(GC.Spread.Sheets.HorizontalAlign.center);
    rowRange.setBorder(borders.BLACK_MEDIUM, { outline: true });
    // Record Status indicator
    rowRange.setBorder(getCalibrationRecordStatusBorderColor(changeLog.RecordStatus), { left: true });
  } catch (err) {
    console.error(`Unable to build beta change log`, { calibration: changeLog }, err);
  }

  rowIndex += takenRows;

  return rowIndex;
}

function createLevelTwoCalibration(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  changeLog: any,
  isLastOfList: boolean
): number {
  let takenCols = 0;

  // User info & Dates
  const date = new Date(changeLog.DateTime.replace('T', 'Z')).toString().substr(0, 21);
  sheet
    .getCell(rowIndex, startCol + takenCols)
    .text(`${changeLog.UserName || 'User ???'} on ${date}`)
    .foreColor(exportChangeLogColours.levelTwo.text)
    .font(`${FONT_SIZE}pt ${FONT_NAME}`);
  sheet.addSpan(rowIndex, startCol + takenCols, 1, COLSPAN);
  takenCols += COLSPAN;

  // Comment
  setChangeLogComment(sheet.getCell(rowIndex, startCol + takenCols), changeLog.Comment);
  sheet.addSpan(rowIndex, startCol + takenCols, 1, COLSPAN);
  takenCols += COLSPAN;

  // Summary
  sheet
    .getCell(rowIndex, startCol + takenCols)
    .text(getChangeLogSummary(changeLog.Summary))
    .foreColor(exportChangeLogColours.levelTwo.summary)
    .font(`${FONT_SIZE}pt ${FONT_NAME}`);
  sheet.addSpan(rowIndex, startCol + takenCols, 1, COLSPAN);
  takenCols += COLSPAN;

  // Styling
  const rowRange = sheet.getRange(rowIndex, startCol, 1, takenCols);
  rowRange
    .backColor(changeLogColours.LevelTwoColour)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center);

  rowRange.setBorder(borders.DARK_GREY_THIN, { top: true, bottom: true });
  rowRange.setBorder(borders.BLACK_MEDIUM, { left: true, right: true, bottom: isLastOfList });

  rowIndex++;

  return rowIndex;
}
